import React, { Component } from "react";
import styled from "styled-components";
import { storage, db } from "../config";
import uid from "uid";
import DatePicker from "./DataPicker";
import ReCAPTCHA from "react-google-recaptcha";
import { highestQualification, ngStates } from "../selectData";
import { withRouter } from "react-router-dom";
import loader from "../Ellipsis-1s-200px.svg";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "isomorphic-unfetch";
import successEmail from "../utils/successEmail";
import fileErrorCheck from "../utils/fileErrorCheck";
import { validateForm } from "../utils/validation";

const msg = {
  applied: "You have already applied for this position: can only apply once.",
  fieldError: "Please make sure all fields are correctly filled",
  submit: "Your application has successfully been submitted"
};

const Segment = styled.div`
  border: 1px solid #e7e9ed;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
`;

class Register extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uploading: false,
      isLoading: false,
      submitTriggered: false,
      cvFilename: "",
      picFilename: "",
      uploadError: {},
      fieldsError: [],
      dateOfBirth: "",
      graduationDate: "",
      cv: "",
      pic: "",
      captchaSuccess: false,
      formData: {
        firstname: this.props.firstName || "",
        lastname: this.props.lastName || "",
        email: this.props.email || "",
        phonenumber: this.props.phoneNumber || "",
        highestQualification: "",
        stateOfOrigin: ""
      }
    };
    this.handleUpload = this.handleUpload.bind(this);
    this.handleGetDate = this.handleGetDate.bind(this);
    this.handleCaptcha = this.handleCaptcha.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleChange = this.handleChange.bind(this);
  }

  handleSubmit(e) {
    e.preventDefault();
    if (this.props.hasApplied) return;

    this.setState({ isLoading: true, submitTriggered: true });
    const {
      formData,
      cv,
      pic,
      dateOfBirth,
      graduationDate,
      captchaSuccess
    } = this.state;
    const hasError = validateForm(formData);

    if (!captchaSuccess || !cv || !pic || !dateOfBirth || hasError.length) {
      this.setState({
        fieldsError: hasError,
        isLoading: false
      });
      toast.error(msg.fieldError);
      return;
    }

    const { match, history } = this.props;
    const id = match.params.id; // use job id as collection name
    const reference = uid(10);

    db
      .collection(id)
      .add({
        cv,
        pic,
        graduationDate,
        id: reference,
        age: getAge(dateOfBirth),
        postedAt: moment.now(),
        ...formData
      })
      .then(() => {
        this.setState({ isLoading: false });
        toast.success(msg.submit);
      })
      .then(() => {
        successEmail(formData.email, reference);
      })
      .then(() => {
        setTimeout(() => history.push("/vacancies"), 3000);
      })
      .catch(e => {
        toast.error("There was an error: please try again");
        this.setState({ isLoading: false, error: e });
      });
  }

  handleCaptcha(value) {
    this.setState({ captchaSuccess: true });
  }

  handleGetDate(name) {
    return date => {
      this.setState({
        [name]: moment(date).format("L")
      });
    };
  }

  handleChange(name) {
    return e => {
      this.setState({
        formData: Object.assign({}, this.state.formData, {
          [name]: e.target.value
        })
      });
    };
  }

  handleUpload(name) {
    if (this.props.hasApplied) return;
    return e => {
      const files = e.target.files;
      const file = files[0];
      const size = parseFloat(file.size / 1024).toFixed(2); // in kilobyte
      // check file errors like size and format
      // return an error of errors
      const fileErrors = fileErrorCheck(name, size, file.type);

      if (fileErrors.includes("fileSize")) {
        return fileSizeErrorMessage(name);
      }

      if (fileErrors.includes("fileType")) {
        const fieldName = name === "pic" ? "passport" : "cv";
        return fileTypeErrorMessage(fieldName);
      }

      this.setState({
        uploading: true,
        [name + "Filename"]: file.name
      });

      const ref = storage.ref();
      const storageRef = ref.child("/docs/" + uid() + file.name);

      storageRef
        .put(file)
        .then(snap => {
          this.setState({
            [name]: snap.downloadURL,
            uploading: false
          });
        })
        .catch(err => {
          console.error(err);
          this.setState({
            uploadError: { name, err },
            uploading: false
          });
        });
    };
  }

  render() {
    const {
      cvFilename,
      picFilename,
      fieldsError,
      uploadError,
      isLoading,
      uploading,
      formData: { firstname, lastname, phonenumber, email }
    } = this.state;
    return (
      <Segment className="mt4 pa3 pa4-ns bg-white">
        <ToastContainer position="top-center" />
        <form onSubmit={this.handleSubmit}>
          <div className="field-body mb3">
            <div className="field">
              <label className="fw6">First Name</label>
              <input
                required
                className="input"
                type="text"
                value={firstname}
                onChange={this.handleChange("firstname")}
              />
              {fieldsError.includes("firstname") && (
                <FieldErrorMessage message="This field is required" />
              )}
            </div>

            <div className="field">
              <label className="fw6">Middle Name (Optional)</label>
              <input
                className="input"
                type="text"
                onChange={this.handleChange("middlename")}
              />
            </div>

            <div className="field">
              <label className="fw6">Surname</label>
              <input
                required
                className="input"
                type="text"
                value={lastname}
                onChange={this.handleChange("lastname")}
              />
              {fieldsError.includes("lastname") && (
                <FieldErrorMessage message="This field is required" />
              )}
            </div>
          </div>

          <div className="field-body mb3 mt2">
            <div className="field">
              <label className="fw6">Phone number</label>
              <input
                className="input"
                type="tel"
                value={phonenumber}
                onChange={this.handleChange("phonenumber")}
              />
              {fieldsError.includes("phonenumber") && (
                <FieldErrorMessage message="This field must be a valid phone number" />
              )}
            </div>

            <div className="field">
              <label className="fw6">Email</label>
              <input
                required
                className="input"
                type="email"
                value={email}
                onChange={this.handleChange("email")}
              />
              {fieldsError.includes("email") && (
                <FieldErrorMessage message="This field must be a valid email address" />
              )}
            </div>

            <div className="field">
              <label className="fw6">Date of birth</label>
              <DatePicker getDate={this.handleGetDate("dateOfBirth")} />
            </div>
          </div>

          <div className="field-body mb3 mt2">
            <div className="field">
              <label className="fw6">Years of Experience</label>
              <div className="control">
                <div
                  className="select"
                  onChange={this.handleChange("yearsOfExperience")}
                >
                  <select style={{ width: 200 }}>
                    <option value="0">0</option>
                    <option value="1-3 years">1-3 years</option>
                    <option value="4-7 years">4-7 years</option>
                    <option value="8-15 years">8-15 years</option>
                    <option value="15 years">15 years and above</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="fw6">Country</label>
              <div className="control">
                <div className="select">
                  <select style={{ width: 200 }}>
                    <option value="Nigeria">Nigeria</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="fw6">State of Origin</label>
              <div className="control">
                <div className="select">
                  <select
                    required
                    style={{ width: 200 }}
                    onChange={this.handleChange("stateOfOrigin")}
                  >
                    <option>Select</option>
                    {ngStates.map((st, i) => (
                      <option key={i} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="ba b--near-white ph3 pv3">
            <div className="field-body mb3">
              <div className="field">
                <label className="fw6">Highest Qualification</label>
                <div className="control">
                  <div className="select">
                    <select
                      required
                      style={{ width: 180 }}
                      onChange={this.handleChange("highestQualification")}
                    >
                      <option>Select</option>
                      {highestQualification.map((x, i) => (
                        <option key={i} value={x}>
                          {x}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="field">
                <label className="fw6">Course of study</label>
                <input
                  required
                  className="input"
                  onChange={this.handleChange("course")}
                />
              </div>

              <div className="field">
                <label className="fw6">Grade obtained</label>
                <div className="control">
                  <div className="select">
                    <select onChange={this.handleChange("grade")}>
                      <option>select</option>
                      <option value="4.5">First class</option>
                      <option value="3.5">Second class upper division</option>
                      <option value="2.4">Second class lower division</option>
                      <option value="4.5">Disctinction</option>
                      <option value="3.5">Upper Credit</option>
                      <option value="2.4">Lower Credit</option>
                      <option value="0">Others</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="field">
              <label className="fw6">Year of Graduation</label>
              <DatePicker getDate={this.handleGetDate("graduationDate")} />
            </div>

            <div className="field">
              <label className="fw6">Other Qualifications (Optional)</label>
              <textarea
                className="textarea"
                type="text"
                onChange={this.handleChange("otherQualifications")}
              />
            </div>
          </div>

          <div className="field mt2 mt3">
            <label className="fw5">
              Cognate Experience (not more than 400 characters.)
            </label>
            <textarea
              className="textarea"
              onChange={this.handleChange("experienceDescription")}
            />
            {fieldsError.includes("experienceDescription") && (
              <FieldErrorMessage message="must be less than 400 characters." />
            )}
          </div>

          <h2 className="mt3 fw5">Upload Document (max size: 600kb)</h2>
          {uploading && <img src={loader} className="h2 w2" alt="" />}
          <div className="field-body mb4 mw5 pb0">
            <div className="field">
              <p className="mb1 f6 mt2 fw5">CV (must be pdf or word)</p>
              {uploadError.name === "cv" && (
                <FieldErrorMessage message="error uploading file" />
              )}
              <div className="file is-boxed">
                <label className="file-label">
                  <input
                    className="file-input"
                    type="file"
                    name="resume"
                    onChange={this.handleUpload("cv")}
                  />
                  <span className="file-cta bg-white">
                    <span className="file-icon">
                      <i className="fa fa-upload" />
                    </span>
                    <span className="file-label f6">Choose a file…</span>
                  </span>
                  {cvFilename && (
                    <span className="file-name">{cvFilename}</span>
                  )}
                </label>
              </div>
            </div>

            <div className="field">
              <p className="mb1 mt2 f6 fw5">Passport/Photo</p>
              {uploadError.name === "pic" && (
                <FieldErrorMessage message="error uploading file" />
              )}
              <div className="file is-boxed">
                <label className="file-label">
                  <input
                    className="file-input"
                    type="file"
                    name="resume"
                    onChange={this.handleUpload("pic")}
                  />
                  <span className="file-cta bg-blue white">
                    <span className="file-icon">
                      <i className="fa fa-upload" />
                    </span>
                    <span className="file-label f6">Choose a file…</span>
                  </span>
                  {picFilename && (
                    <span className="file-name">{picFilename}</span>
                  )}
                </label>
              </div>
            </div>
          </div>

          <ReCAPTCHA
            sitekey="6LcawEwUAAAAAGOC_qebUxjYPSFje3-eVVAkLk4P"
            ref="recaptcha"
            onChange={this.handleCaptcha}
          />
          <button
            className={`button is-link ph5 mt4 ${isLoading && "is-loading"}`}
          >
            Submit
          </button>
        </form>
      </Segment>
    );
  }
}

export default withRouter(Register);

const FieldErrorMessage = ({ message }) => <p className="f6 red">{message}</p>;

const fileSizeErrorMessage = name => {
  toast.error(`${name} size too large, should be less than 
  ${name === "cv" ? "600kb" : "300kb"}`);
};

const fileTypeErrorMessage = name => {
  toast.error(`Invalid file type for ${name} 
  ${name === "cv" ? "must be word or pdf" : ""}`);
};

const getAge = function(date) {
  return Math.abs(moment(date).diff(moment(), "years"));
};



// WEBPACK FOOTER //
// ./src/components/ApplicationForm.js